import { ApiError } from "@hakili/api-client";
import { t } from "@hakili/i18n";
import { Button, Card, Field, GuardedButton } from "@hakili/ui";
import { useState } from "react";

import { CODE_DEMANDE, reasonOf } from "@hakili/app-shell";

/**
 * `P0-UIK-015`: the console login screen.
 *
 * `WF-CON-0` explains why it exists at all: *"the console mockup has no login
 * screen. Implement authentication through the `identity` module (email plus
 * password, optional TOTP, JWT access and refresh), and derive the view from
 * the user's role."* The mockup's `Vue` switcher is the demonstration device
 * this screen replaces.
 *
 * **Danger sentence.** This screen's whole job on a bad day is to show the
 * server's own sentence. 4.5.5 writes each refusal's French beside the guard
 * that produced it , *"Identifiants incorrects"*, *"Trop de tentatives"*,
 * *"Code incorrect. Il vous reste N essai(s)"* , and those sentences differ in
 * ways that tell a user what to do next. A screen that renders its own generic
 * *"la connexion a échoué"* over all of them turns a locked account, a wrong
 * password and a network failure into one indistinguishable dead end, and the
 * person telephones instead of waiting fifteen minutes.
 *
 * So `ApiError.message` is rendered verbatim, and the only sentence this file
 * chooses is the one for a failure that reached no server at all.
 *
 * **The TOTP field appears only when the server asks for it**, which the
 * server says with `details.reason = "totp_code_required"`.
 * Showing it always would ask every user for a code most of them do not have;
 * 6.1.2 makes TOTP optional for every role except `administration`.
 */

export interface ConnexionProps {
  /** Performs the login. Injected, so this screen needs no client and no origin. */
  readonly connecter: (identifiants: { email: string; password: string; totpCode?: string }) => Promise<void>;
  /**
   * `P0-CNW-028`: why the screen is being shown, when it is shown to somebody
   * the api authenticated and this surface will not admit. **Never a silent
   * redirect**: a person who lands on a login with no sentence retypes the
   * password they just used successfully.
   */
  readonly motif?: string;
  /** Rendered on a failure that never reached the server (4.6.6: never a dead end). */
  readonly messageReseau?: string;
}

export function Connexion({ connecter, messageReseau, motif }: ConnexionProps): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [code, setCode] = useState("");
  const [codeDemande, setCodeDemande] = useState(false);
  const [refus, setRefus] = useState("");
  const [enCours, setEnCours] = useState(false);

  // Typed as what it USES rather than as React's `FormEvent`, which React 19
  // deprecates as a name for an event that does not exist. The handler needs
  // one method; asking for more would be asking for a type that moves.
  async function soumettre(event: Pick<Event, "preventDefault">): Promise<void> {
    event.preventDefault();
    setRefus("");
    setEnCours(true);
    try {
      await connecter({ email, password: motDePasse, ...(code === "" ? {} : { totpCode: code }) });
    } catch (cause) {
      if (cause instanceof ApiError) {
        // The server's own sentence, untouched. The code decides only whether
        // to reveal the second-factor field.
        setRefus(cause.message);
        // `details.reason`, not `code`. This screen read `code ===
        // "totp_required"` until `P0-UIK-021` mounted it against a live API and
        // found the string is declared nowhere: 4.5.5's registry has no such
        // code, and `domain/login.ts` answers `forbidden` with
        // `details.reason = "totp_code_required"`. The screen therefore showed
        // the refusal and never revealed the field that answers it , a user
        // with TOTP enabled could not log in at all. Two isolated component
        // specs and an integration spec had all passed: each asserted the
        // branch it was written beside, and none crossed the seam.
        if (reasonOf(cause) === CODE_DEMANDE) setCodeDemande(true);
      } else {
        // No server was reached, so there is no sentence to show and the app
        // supplies one. This is the ONLY message this screen chooses.
        setRefus(messageReseau ?? t("common.error.reseau"));
      }
    } finally {
      setEnCours(false);
    }
  }

  const incomplet = email.trim() === "" || motDePasse === "";

  return (
    <Card shape="console" className="mx-auto mt-16 flex w-full max-w-sm flex-col gap-4">
      <h1 className="text-lg font-semibold text-gray-900">{t("console.connexion.titre")}</h1>

      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          void soumettre(event);
        }}
      >
        <Field label={t("console.connexion.email")} htmlFor="connexion-email">
          <input
            id="connexion-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          />
        </Field>

        <Field label={t("console.connexion.motDePasse")} htmlFor="connexion-mot-de-passe">
          <input
            id="connexion-mot-de-passe"
            type="password"
            autoComplete="current-password"
            value={motDePasse}
            onChange={(event) => {
              setMotDePasse(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          />
        </Field>

        {codeDemande ? (
          <Field label={t("console.connexion.code")} htmlFor="connexion-code">
            {/* 4.6.7's input rules: the numeric keypad without a tap, and the
                one-time-code hint so the handset offers the SMS or authenticator
                value. Cheap here, and invisible until it is missing. */}
            <input
              id="connexion-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
          </Field>
        ) : null}

        {refus === "" && motif === undefined ? null : (
          <p role="alert" className="text-sm text-rose-700" data-testid="connexion-refus">
            {refus === "" ? motif : refus}
          </p>
        )}

        {/* I19, and `Button` deliberately has no `disabled` prop: the ONLY way
            to render an unavailable action in this kit is a `GuardedButton`,
            which cannot be rendered without a reason. Two states, two reasons. */}
        {enCours ? (
          <GuardedButton disabled reason={t("console.connexion.enCours")}>
            {t("console.connexion.entrer")}
          </GuardedButton>
        ) : incomplet ? (
          <GuardedButton disabled reason={t("common.state.vide")}>
            {t("console.connexion.entrer")}
          </GuardedButton>
        ) : (
          <Button type="submit">{t("console.connexion.entrer")}</Button>
        )}
      </form>
    </Card>
  );
}
